/* eslint-disable max-len */
/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  connect, useSelector, shallowEqual, useDispatch
} from 'react-redux';
import swal from 'sweetalert';
import _ from 'lodash';
import { mapDispatchToProps } from '../../../store/externalCalls/api/formApi';
import { composeURL, checkObjectAttributes } from '../../../utils/urlTools';
import PageTitle from '../../organisms/pageTitle/pageTitle';
import SimplePageTitle from '../../molecules/pageTitle/pageTitle';
import { storeStructure } from '../../../store/reducers';
import FormGroup from '../../organisms/formGroup/formGroup2';
import { updateForm } from '../../../store/actions/formActions';
import FooterButton from '../../molecules/formFooter/formFooter';
import PreventTransitions from '../../molecules/preventTransitions/preventTransitions';
import { resetError } from '../../../store/actions/errorActions';
import { updateAdditionalMetadata } from '../../../store/actions/additionalMetadataActions';
import { useParams, useNavigate } from 'react-router-dom';

const AdminFormTemplate = (props) => {
  const {
    getItem,
    getAdditionalMetadata,
    resetAdditionalMetadata,
    resetForm,
    updateItem,
    updateItemWithRefresh,
    deleteItem,
    createItem,
    formEdited,
    configFile,
    history,
    match,
  } = (props);

  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const session = useSelector((state) => state[storeStructure.Session], shallowEqual);
  const values = useSelector((state) => state[storeStructure.FormData], shallowEqual);
  const additionalMetadata = useSelector(
    (state) => state[storeStructure.AdditionalMetadata], shallowEqual,
  );
  const itemChanged = useSelector((state) => state.session.isBlocking);
  const [errors, setErrors] = useState({});
  const id = configFile.idAttribute !== undefined
    ? params[configFile.idAttribute] : undefined;


  const savedCallback = () => {
    swal({
      title: 'Good job!',
      text: 'The form was saved!',
      icon: 'success',
    }).then(() => {
      if (configFile.goBack) {
       navigate(-1);
      }
    });
    formEdited(false);
  };

  const handlerErrors = (e) => {
    const messages = e.errors === undefined ? { general: 'Something was wrong' } : e.errors;

    setErrors(messages);
    setTimeout(() => {
      setErrors({});
    }, 5000);
  };

  const delayedQuery = useCallback(_.debounce((valueChanged) => {
    const { url, ...rest } = valueChanged;
    dispatch(updateForm(valueChanged));
    formEdited();
  }, 300), [session, configFile]);


  const handlerCreateWithRefresh = (valueChanged) => {
    const newValues = { ...values, ...valueChanged };
    const { url, ...rest } = newValues;
    if (id !== 'new') {
      updateItemWithRefresh(`${composeURL(session, configFile.sessionParams, configFile.updateURL)}/${values.id}`, rest)
        .then(() => {
          // dispatch(updateForm(newValues));
        }, (e) => {
          handlerErrors(e);
        });
    } else {
      dispatch(updateForm(newValues));
      formEdited();
    }
  };
  const handlerOnChange = (valueChanged) => {
    const newValues = { ...values, ...valueChanged };
    if (valueChanged.url) {
      newValues.composeExperienceUrls = newValues.url;
      delete newValues.url;
    }
    delayedQuery(newValues);
  };

  const handlerOnDelete = (valueDeleted, attributeName) => {
    const urlName = (attributeName === 'composeExperiencesUrls') ? 'composeExperienceUrl' : attributeName;
    
    if (id !== 'new') {
      swal({
        title: 'Are you sure?',
        text: 'You are removing a item.',
        icon: 'warning',
        buttons: true,
        dangerMode: true,
      }).then((willDelete) => {
        if (willDelete) {
          deleteItem(`${composeURL(session, configFile.sessionParams, urlName)}/${valueDeleted.id}`)
            .then(() => {
              const url = composeURL(session, configFile.sessionParams, configFile.getURL, id);
              getItem(url);
            }, (e) => {
              handlerErrors(e);
            });
        }
      });
    } else {
      const composeExperiences = values[attributeName].filter((item) => item.url !== valueDeleted.url);
      const newValues = { ...values, ...{ composeExperiencesUrls: composeExperiences } };
      dispatch(updateForm(newValues));
      formEdited();
    }
  };
  /**
   * Create a object with attributes required by the config file and default value
   * @param {*} values
   */
  const pushAllValues = () => {
    const allValues = values;
    const formConfigs = Array.isArray(configFile.formConfig)
      ? configFile.formConfig : [configFile.formConfig];

    formConfigs.forEach((formConfig) => {
      formConfig.fields.forEach((field) => {
        if (!allValues[field.attributeName] && field.default) {
          allValues[field.attributeName] = field.default;
        }
      });
    });

    return allValues;
  };

  const callbackOfButtonSave = (newElement) => {
      savedCallback();
  };

  const handlerSaveButton = () => {
    setErrors({});
    dispatch(resetError());
    if (id === 'new') {
      createItem(composeURL(session, configFile.sessionParams, configFile.updateURL), pushAllValues())
        .then((newElement) => callbackOfButtonSave(newElement), (e) => handlerErrors(e));
    } else {
      updateItem(`${composeURL(session, configFile.sessionParams, configFile.updateURL)}/${values.id}`, values)
        .then((updatedElement) => callbackOfButtonSave(updatedElement), (e) => handlerErrors(e));
    }
  };

  useEffect(() => {
    setErrors({});
    if (checkObjectAttributes(session, configFile.sessionParams)) {
      if (id !== 'new') {
        const url = composeURL(session, configFile.sessionParams, configFile.getURL, id);
        getItem(url);
      }
    }
    return function cleanup() {
      resetForm();
      resetAdditionalMetadata();
    };
  }, [getItem, session.areaId, session.appId]);

  return (
    <div>
      { configFile.noArea
      && (
        <SimplePageTitle>{configFile.title}</SimplePageTitle>
      )}
      { (configFile.noArea === undefined || configFile.noArea === false)
      && (
        <PageTitle>{configFile.title}</PageTitle>
      )}
      { Object.keys(errors).length > 0
      && (
        <div className="alert alert-danger" role="alert">
          { Object.keys(errors).map((i) => <div key={`error-${i}`}>{errors[i]}</div>)}
        </div>
      )}
      { Array.isArray(configFile.formConfig) === true
      && configFile.formConfig.map((formGroup) => (
        <FormGroup
          key={`${configFile.attributeName}-${formGroup.attributeName}`}
          configFile={formGroup}
          value={values}
          onChange={handlerOnChange}
          onCreateWithRefresh={handlerCreateWithRefresh}
          onDeleteInputRelationship={handlerOnDelete}
        />
      ))}
      <PreventTransitions />
      {itemChanged === true
      && (
         <div class="footer-section">
          <FooterButton onClick={handlerSaveButton}>Save</FooterButton>
         </div>
      )}
    </div>
  );
};

AdminFormTemplate.propTypes = {
  configFile: PropTypes.shape({
    attributeName: PropTypes.string,
    title: PropTypes.string,
    sessionParams: PropTypes.arrayOf(PropTypes.string),
    getURL: PropTypes.string,
    additionaMetadataURL: PropTypes.string,
    updateURL: PropTypes.string,
    removeURL: PropTypes.string,
    formConfig: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.arrayOf(PropTypes.shape({}))]),
    idAttribute: PropTypes.string,
    goBack: PropTypes.bool,
    noArea: PropTypes.bool,
  }).isRequired,
  getItem: PropTypes.func.isRequired,
  getAdditionalMetadata: PropTypes.func.isRequired,
  resetAdditionalMetadata: PropTypes.func.isRequired,
  resetForm: PropTypes.func.isRequired,
  updateItem: PropTypes.func.isRequired,
  updateItemWithRefresh: PropTypes.func.isRequired,
  deleteItem: PropTypes.func.isRequired,
  createItem: PropTypes.func.isRequired,
  formEdited: PropTypes.func.isRequired,
  location: PropTypes.shape({
    pathName: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({}),
  }).isRequired,
  history: PropTypes.shape({
    goBack: PropTypes.func,
  }),
};

AdminFormTemplate.defaultProps = {

};


export default (connect(null, mapDispatchToProps)(AdminFormTemplate));
